import type { ChannelEnvelope, InvitePresentation, MatchSummary, RoomInfo } from "@guandan/protocol";

/**
 * Typed wrapper over the Worker's REST surface.
 *
 * Every mutation goes out as JSON from the same origin, because the server
 * rejects anything else — that pair is its CSRF defence, so the client must not
 * quietly send a form-encoded body and wonder why it gets a 400.
 */

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message?: string) {
    super(message ?? code);
    this.name = "ApiError";
  }
}

/**
 * D1's replication bookmark: our place in the database's log.
 *
 * Reads are answered by whichever replica is closest, which is most of why the
 * social screens are quick — but a replica can be a moment behind. The server
 * returns this on every response and we send it back on the next request, so a
 * follow immediately followed by a list read cannot show a list without the
 * person we just followed in it.
 *
 * Deliberately in memory only. It is meaningless across sessions, and a stale
 * one from `sessionStorage` would pin reads to an old position for no benefit.
 */
let bookmark: string | null = null;
const BOOKMARK_HEADER = "x-d1-bookmark";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const mutating = method !== "GET" && method !== "HEAD";
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      // Every mutation must declare JSON, even one with no body: the server
      // pairs that with a same-origin check as its CSRF defence and rejects
      // anything else outright. Bodyless POSTs and DELETEs are the easy ones to
      // get wrong, because they have no body to hang the header off.
      ...(mutating ? { "content-type": "application/json" } : {}),
      ...(bookmark === null ? {} : { [BOOKMARK_HEADER]: bookmark }),
      ...init.headers,
    },
  });
  const advanced = response.headers.get(BOOKMARK_HEADER);
  if (advanced !== null && advanced !== "") bookmark = advanced;
  const text = await response.text();
  let body: unknown = {};
  if (text !== "") {
    try { body = JSON.parse(text) as unknown; } catch { /* non-JSON 4xx/5xx responses are still ApiErrors */ }
  }
  if (!response.ok) {
    const code = typeof body === "object" && body !== null && "error" in body
      ? String((body as { error: unknown }).error)
      : `http_${response.status}`;
    const message = typeof body === "object" && body !== null && "message" in body && typeof (body as { message: unknown }).message === "string"
      ? (body as { message: string }).message
      : undefined;
    throw new ApiError(response.status, code, message);
  }
  return body as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: "POST", ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// --- shapes ------------------------------------------------------------------

export interface User {
  id: string;
  /** Null for a guest: the account has no address behind it yet. */
  email: string | null;
  isGuest: boolean;
  displayName: string;
  avatarId: string;
  locale: "en" | "zh-CN";
  hasPassword: boolean;
}

export interface DailyStanding {
  rank: number;
  userId: string;
  displayName: string;
  avatarId: string;
  position: number;
  upgrade: number;
  won: boolean;
  durationMs: number;
  bombs: number;
}

export interface DailyAttemptView {
  state: "playing" | "complete" | "aborted";
  roomId: string;
  position: number | null;
  upgrade: number | null;
  won: boolean | null;
  durationMs: number | null;
  bombs: number | null;
  rank: number | null;
  players: number | null;
}

export interface DailyView {
  day: { id: string; opensAt: number; closesAt: number };
  attempt: DailyAttemptView | null;
  standings: DailyStanding[];
}

export interface ProfilePatch {
  displayName?: string;
  avatarId?: string;
  locale?: "en" | "zh-CN";
}

export interface Progress {
  level: number;
  xp: number;
  coins: number;
  gamesPlayed: number;
  gamesWon: number;
  handsWon: number;
  bombsPlayed: number;
  highestLevelReached: number;
  rating: number;
  highestRating: number;
  ratedMatches: number;
  ratingSeasonId: string;
  weeklyMissions: {
    weekId: string;
    validMatches: number;
    firstMilestoneClaimed: boolean;
    secondMilestoneClaimed: boolean;
    nextResetAt: number;
  };
  currentRoomId: string | null;
}

export interface CoinTier {
  id: string;
  label: string;
  entryCoins: number;
  requiredBalance: number;
  requiredLevel: number;
}

export interface RulesetSummary {
  id: string;
  label: string;
  tribute: boolean;
  aAttempts: number;
  turnMs: number;
}

/**
 * A global notice the player has not dismissed yet, already resolved to their
 * own language by the server. Null for almost every session.
 */
export interface Announcement {
  id: string;
  title: string;
  body: string;
  kind: "info" | "update" | "maintenance";
  publishedAt: number;
}

export interface Me {
  user: User;
  progress: Progress;
  currentRoom: string | null;
  availableTiers: CoinTier[];
  /** Sent with the session so the Friends tab's badge does not need its own request. */
  social?: { followers: number; following: number; friends: number };
  /** Carried with the session so the lobby renders it, rather than dropping it over the lobby. */
  announcement?: Announcement | null;
  dailyClaimed: boolean;
  firstWinClaimed: boolean;
}

export interface PlayerStats {
  generatedAt: number;
  overview: {
    matches: number; wins: number; losses: number; winRate: number; handsPlayed: number; handsWon: number;
    handWinRate: number; averageDurationMs: number; fastestMatchMs: number | null; longestMatchMs: number | null;
  };
  play: {
    totalPlays: number; cardsPlayed: number; passes: number; bombs: number; firstOuts: number; trickWins: number;
    maxBombPower: number; maxComboType: string | null; averageUpgrade: number;
  };
  competition: {
    currentRating: number; highestRating: number; ratingDelta: number; matchWinRate: number; handWinRate: number;
    trend: { endedAt: number; ratingDelta: number; won: boolean }[];
  };
  economy: {
    coins: number; xp: number; rating: number; averageCoins: number; averageXp: number;
    recentRewards: { endedAt: number; coins: number; xp: number; rating: number }[];
  };
  recentMatches: {
    roomId: string; endedAt: number; won: boolean; handsPlayed: number; durationMs: number; levels: number[];
    upgrades: number; bombs: number; coins: number; xp: number; ratingDelta: number;
  }[];
}

export type MatchMode = "ranked" | "gold";

export interface QueueStatus {
  ticketId?: string;
  /**
   * `prepping` means a table has been assembled and is waiting on ready-checks.
   * The room does not exist yet — `lobbyCode` is where the player goes, not
   * `roomId`, which is only reserved at this point.
   */
  state: "none" | "queued" | "forming" | "prepping" | "matched" | "cancelled" | "declined" | "failed";
  joinedAt?: number;
  aiBackfillAt?: number;
  roomId?: string;
  lobbyCode?: string | null;
  readyDeadline?: number | null;
  failureCode?: string;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  avatarId: string;
  level: number;
  rating: number;
  matches: number;
  wins: number;
  losses: number;
  isMe: boolean;
}

export interface Leaderboard {
  season: { id: string; label: string; startsAt: number; endsAt: number };
  top: LeaderboardEntry[];
  me: LeaderboardEntry | null;
  neighbours: LeaderboardEntry[];
}

export interface LobbyMember {
  userId: string;
  name: string;
  avatarId: string;
  level: number;
  rating: number;
  seat: number | null;
  ready?: boolean;
}

export interface LobbyInfo {
  id: string;
  code: string;
  hostUserId: string;
  rulesetId: string;
  coinTier: string;
  entryCoins: number;
  mode: MatchMode;
  /** `private` is host-started from a code; `ready` is a matchmade ready-check. */
  kind: "private" | "ready";
  state: "open" | "starting" | "matched";
  expiresAt: number;
  readyDeadline: number | null;
  /**
   * Reserved when the lobby is created, so it is set long before the room
   * exists. Only `state === "matched"` says the room has actually been dealt in
   * — navigating on this field alone lands the first player to press Ready on a
   * table that is not there yet.
   */
  roomId: string | null;
  visibility?: "public" | "private";
  members: LobbyMember[];
}

/** Present only when the server is running with DEV_AUTH=1. */
export interface CodeRequest {
  ok: boolean;
  message?: string;
  delivery?: "sent" | "dev";
  devCode?: string;
  expiresAt?: number;
}

/**
 * What every way into a match asks for.
 *
 * `coinTier` is omitted for ranked, which the server rejects a stake on rather
 * than ignoring one — see `matchOptions` in the Worker.
 */
export interface MatchOptions {
  mode: MatchMode;
  rulesetId: string;
  coinTier?: string;
}

export interface ReplayCursor { before: number; beforeRoom: string }

export interface ReplayRow {
  roomId: string;
  endedAt: number;
  mode: MatchMode;
  coinTier: string;
  hands: number;
  rulesetId: string;
  seat: number;
  won: boolean;
  shared: boolean;
  shareSlug: string | null;
}

export type Presence = "offline" | "online" | "inLobby" | "inMatch";

export interface SocialPlayer {
  userId: string;
  name: string;
  avatarId: string;
  level: number;
  rating: number;
  matches: number;
  wins: number;
  winRate: number;
  playerCode: string | null;
  presence?: Presence;
  isMe?: boolean;
  following?: boolean;
  followsMe?: boolean;
  friends?: boolean;
}

export interface Relationship { following: boolean; followsMe: boolean; friends: boolean }

export interface ReplayCard {
  roomId: string;
  endedAt: number;
  mode: string;
  rulesetId: string;
  hands: number;
  winner: number;
  durationMs: number;
  levels: number[];
  players: { seat: number; team: number; userId: string; name: string; avatarId: string }[];
}

export interface SocialFeed {
  players: SocialPlayer[];
  shares: { roomId: string; slug: string; sharedBy: string; sharedAt: number; card: ReplayCard | null }[];
  cursor?: number | null;
}

export type RelationshipKind = "friends" | "following" | "followers";

/** An invitation, as the server's state machine holds it. */
export type GameInvite = InvitePresentation;

export interface FeedItem {
  id: string;
  type: string;
  actorId: string;
  entityType: string;
  entityId: string;
  occurredAt: number;
  payload: unknown;
}

export interface ChannelBootstrap {
  protocol: number;
  serverTime: number;
  cursor: number;
  unread: number;
  social: { followers: number; following: number; friends: number };
  invites: GameInvite[];
}

export type Activity =
  | { type: "idle" }
  | { type: "queue"; ticketId: string; state: QueueStatus["state"]; joinedAt: number; fillAt: number; config: MatchOptions; lobbyCode?: string | null }
  | { type: "party"; partyId: string; code: string; hostUserId: string; members: Array<{ userId: string; name: string; rating: number }> }
  | { type: "lobby"; code: string; kind: "private" | "ready"; role: "host" | "member"; mode: MatchMode; visibility: "public" | "private" }
  | { type: "match"; roomId: string; away: boolean };

export interface PartyInfo {
  id: string; code: string; hostUserId: string; state?: string; expiresAt?: number;
  members: Array<{ userId: string; name: string; rating: number }>;
}
export interface LiveTable { roomId: string; mode: MatchMode; rulesetId: string; coinTier: string; startedAt: number }
export interface PublicPlayer extends SocialPlayer { canFollow: boolean; canInvite: boolean; canWatch: boolean }
/** One delivered event, as the notifications list shows it. */
export interface UserEvent {
  seq: number;
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  entityVersion: number;
  payload: unknown;
  createdAt: number;
  expiresAt: number | null;
  readAt: number | null;
}

// --- endpoints ---------------------------------------------------------------

export const api = {
  me: () => request<Me>("/me"),
  ackAnnouncement: (id: string) => post<{ ok: true; recorded: boolean }>(`/announcements/${encodeURIComponent(id)}/ack`),
  activity: () => request<Activity>("/me/activity"),
  updateProfile: (patch: ProfilePatch) =>
    request<{ user: User; progress: Progress }>("/me", { method: "PATCH", body: JSON.stringify(patch) }),
  updatePassword: (body: { currentPassword?: string; password: string }, turnstileToken?: string) =>
    request<{ ok: true }>("/me/password", { method: "PATCH", body: JSON.stringify({ ...body, turnstileToken }) }),
  stats: () => request<PlayerStats>("/me/stats"),
  claimDaily: () => post<{ progress: Progress }>("/me/daily-claim"),

  registerCode: (email: string, locale: "en" | "zh-CN" = "en", turnstileToken?: string) => post<CodeRequest>("/auth/register/code", { email, locale, turnstileToken }),
  register: (email: string, code: string, password: string, locale: "en" | "zh-CN" = "en") =>
    post<{ user: User; progress: Progress; availableTiers: CoinTier[] }>("/auth/register/verify", { email, code, password, locale }),
  login: (email: string, password: string, turnstileToken?: string) =>
    post<{ user: User; progress: Progress; availableTiers: CoinTier[] }>("/auth/login", { email, password, turnstileToken }),
  resetCode: (email: string, locale: "en" | "zh-CN" = "en", turnstileToken?: string) => post<CodeRequest>("/auth/password/reset/code", { email, locale, turnstileToken }),
  turnstilePolicy: () => request<{ required: boolean }>("/auth/turnstile-policy"),
  resetPassword: (email: string, code: string, password: string) =>
    post<{ user: User; progress: Progress; availableTiers: CoinTier[] }>("/auth/password/reset", { email, code, password }),
  /** A playable account with no email behind it. */
  guest: () => post<{ user: User; progress: Progress; availableTiers: CoinTier[] }>("/auth/guest"),
  /** Attach an email and password to the guest already signed in, same account. */
  claimGuest: (email: string, code: string, password: string, locale: "en" | "zh-CN" = "en") =>
    post<{ user: User; progress: Progress; availableTiers: CoinTier[] }>("/auth/guest/claim", { email, code, password, locale }),
  logout: () => post<{ ok: true }>("/auth/logout"),

  /** Today's deal, the caller's attempt at it, and the day's ladder. */
  daily: () => request<DailyView>("/daily"),
  /** Start today's deal, or get back the table already in play. */
  playDaily: () => post<{ roomId: string; resumed: boolean }>("/daily/play"),

  coinTiers: () => request<CoinTier[]>("/coin-tiers"),
  rulesets: () => request<RulesetSummary[]>("/rulesets"),
  leaderboard: (limit = 50) => request<Leaderboard>(`/leaderboard?limit=${limit}`),
  liveTables: () => request<{ tables: LiveTable[]; cursor: string | null }>("/live-tables"),
  player: (id: string) => request<PublicPlayer>(`/players/${id}`),

  queueStatus: () => request<QueueStatus>("/match/queue"),
  joinQueue: (options: MatchOptions) => post<QueueStatus>("/match/queue", options),
  leaveQueue: () => request<{ ok: true }>("/match/queue", { method: "DELETE" }),

  createParty: () => post<PartyInfo>("/match/party"),
  party: (id: string) => request<PartyInfo>(`/match/party/${id}`),
  joinParty: (code: string) => post<{ id: string; joined: true }>(`/match/party/${code}/join`),
  leaveParty: (id: string) => request<{ ok: true }>(`/match/party/${id}/members/me`, { method: "DELETE" }),

  createLobby: (options: MatchOptions) => post<LobbyInfo>("/match/lobby", options),
  lobbyInfo: (code: string) => request<LobbyInfo>(`/match/lobby/${code}`),
  joinLobby: (code: string) => post<LobbyInfo>(`/match/lobby/${code}/join`),
  assignSeats: (code: string, assignments: { userId: string; seat: number }[]) =>
    request<LobbyInfo>(`/match/lobby/${code}/seats`, {
      method: "PATCH", body: JSON.stringify({ assignments }),
    }),
  setReady: (code: string, ready: boolean) => post<LobbyInfo>(`/match/lobby/${code}/ready`, { ready }),
  startLobby: (code: string) => post<{ roomId: string; room: RoomInfo }>(`/match/lobby/${code}/start`),
  leaveLobby: (code: string, userId: string) =>
    request<unknown>(`/match/lobby/${code}/members/${userId}`, { method: "DELETE" }),
  setLobbyVisibility: (code: string, visibility: "public" | "private") =>
    request<LobbyInfo>(`/match/lobby/${code}/visibility`, { method: "PATCH", body: JSON.stringify({ visibility }) }),

  quickMatch: (options: MatchOptions) => post<{ roomId: string; room: RoomInfo }>("/rooms", options),
  room: (roomId: string) => request<RoomInfo>(`/rooms/${roomId}`),
  setPresence: (roomId: string, presence: "active" | "away") => post<{ ok: true; away: boolean }>(`/rooms/${roomId}/presence`, { presence }),
  matchSummary: (roomId: string) => request<{ roomId: string; mode: MatchMode; rulesetId: string; coinTier: string; lifecycleState: string; summary: MatchSummary }>(`/matches/${roomId}/summary`),
  rematch: (roomId: string) => post<{ code: string; reused: boolean }>(`/matches/${roomId}/rematch`),

  // --- replays ---------------------------------------------------------------
  replays: (cursor?: ReplayCursor) =>
    request<{ replays: ReplayRow[]; cursor: ReplayCursor | null }>(
      `/replays${cursor ? `?before=${cursor.before}&beforeRoom=${encodeURIComponent(cursor.beforeRoom)}` : ""}`,
    ),
  shareReplay: (roomId: string) => post<{ slug: string; url: string }>(`/matches/${roomId}/share`),
  unshareReplay: (roomId: string) => request<{ ok: true }>(`/matches/${roomId}/share`, { method: "DELETE" }),

  // --- friends ---------------------------------------------------------------
  socialMe: () => request<{ playerCode: string | null; followers: number; following: number; friends: number }>("/social/me"),
  relationships: (kind: RelationshipKind, cursor?: string | null) =>
    request<{ players: SocialPlayer[]; cursor: string | null }>(
      `/social/relationships?kind=${kind}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  searchPlayer: (query: string) => request<{ players: SocialPlayer[] }>(`/social/players/search?q=${encodeURIComponent(query)}`),
  follow: (userId: string) => request<Relationship>(`/social/relationships/${userId}`, { method: "PUT" }),
  unfollow: (userId: string) => request<Relationship>(`/social/relationships/${userId}`, { method: "DELETE" }),
  feed: (cursor?: string | null) =>
    request<{ items: FeedItem[]; cursor: string | null }>(`/social/feed${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),

  /**
   * Send an invitation.
   *
   * The idempotency key belongs to the *user action*, not the request, so a
   * double tap or a retry after a dropped response resolves to the one
   * invitation rather than filling the recipient's panel with copies.
   */
  invite: (userId: string, kind: "duo" | "gold", code: string, idempotencyKey: string) =>
    request<{ ok: true; invite: GameInvite; created: boolean }>("/social/invites", {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify({ userId, kind, code }),
    }),
  invites: () => request<{ invites: GameInvite[] }>("/social/invites"),
  acceptInvite: (inviteId: string) =>
    post<{ ok: true; state: string; destination: { type: "party" | "lobby"; id: string | null; code: string } }>(
      `/social/invites/${inviteId}/accept`,
    ),
  declineInvite: (inviteId: string) => post<{ ok: true }>(`/social/invites/${inviteId}/decline`),
  cancelInvite: (inviteId: string) => post<{ ok: true }>(`/social/invites/${inviteId}/cancel`),

  // --- the channel -----------------------------------------------------------
  bootstrap: () => request<ChannelBootstrap>("/channel/bootstrap"),
  events: () => request<{ events: UserEvent[] }>("/channel/events"),
  sync: (after: number) => request<{ events: ChannelEnvelope[]; cursor: number; more: boolean }>(`/channel/sync?after=${after}`),
  readEvent: (id: string) => post<{ ok: true }>(`/channel/events/${id}/read`),
  readAllEvents: () => post<{ ok: true }>("/channel/read-all"),
};

/**
 * A shared replay, fetched by slug.
 *
 * Deliberately outside `api`: it takes no session and must work for a visitor
 * who has never signed in, so it does not go through the credentialed helper.
 */
export async function fetchSharedReplay(slug: string): Promise<unknown> {
  const response = await fetch(`/api/r/${encodeURIComponent(slug)}`, { cache: "no-store" });
  if (!response.ok) throw new ApiError(response.status, "not_found");
  return response.json();
}

/** Who played a match, for putting names round the replay table. */
export async function fetchReplayCard(id: string, shared: boolean): Promise<ReplayCard | null> {
  const path = shared ? `/api/r/${encodeURIComponent(id)}/meta` : `/api/matches/${encodeURIComponent(id)}/card`;
  const response = await fetch(path, { credentials: "same-origin", ...(shared ? { cache: "no-store" as const } : {}) });
  return response.ok ? await response.json() as ReplayCard : null;
}

export async function fetchOwnReplay(roomId: string): Promise<unknown> {
  const response = await fetch(`/api/matches/${encodeURIComponent(roomId)}/replay`, { credentials: "same-origin" });
  if (!response.ok) throw new ApiError(response.status, "not_found");
  return response.json();
}
